import { useRef, useState, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function CategoryScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener("scroll", checkScroll);
  }, []);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 300;
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <section className="relative w-full py-16 md:py-24 px-6 md:px-12 lg:px-20 bg-background-100 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 md:mb-14">
        <div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
            Todas as<br />
            <span className="italic font-normal">Categorias</span>
          </h2>
          <p className="text-sm text-foreground-500 mt-3 leading-relaxed max-w-md">
            São <strong className="text-accent-700">14 categorias</strong> organizadas com os melhores produtos da planilha CSSBuy para você explorar.
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 md:mt-0">
          <span className="bg-foreground-950 text-background-50 text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap">
            14 Categorias
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-background-200 text-foreground-700 cursor-pointer transition-all hover:bg-background-300 disabled:opacity-30 disabled:cursor-default"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-background-200 text-foreground-700 cursor-pointer transition-all hover:bg-background-300 disabled:opacity-30 disabled:cursor-default"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Cards */}
      <div
        ref={scrollRef}
        className="flex gap-4 md:gap-5 overflow-x-auto scrollbar-hide pb-4 -mx-6 md:-mx-12 lg:-mx-20 px-6 md:px-12 lg:px-20"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {categories.map((cat) => (
          <a
            key={cat.id}
            href={cat.url}
            target="_blank"
            rel="noopener nofollow"
            className="flex-shrink-0 w-[220px] md:w-[260px] rounded-2xl bg-background-50 border border-background-200/50 overflow-hidden group cursor-pointer transition-all hover:border-background-300"
          >
            <div className="h-44 md:h-52 overflow-hidden">
              <img
                src={cat.image}
                alt={cat.namePt}
                title={cat.namePt + " CSSBuy"}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-4 md:p-5">
              <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{cat.namePt}</p>
              <p className="text-xs text-foreground-500 mt-0.5 whitespace-nowrap">{cat.description}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-lg font-heading font-bold text-foreground-950">{cat.count}</span>
                <span className="text-xs font-medium text-accent-700 whitespace-nowrap">Ver →</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}